import EventEmitter from "@dreamworld/event-emitter";
import {
  query,
  queryUpdate,
  queryCancel,
  querySnapshot,
  queryMetadata,
  docsUpdated
} from "./redux/actions.js";

import keyBy from "lodash-es/keyBy";
import map from "lodash-es/map";

//wait time for design docs
const waitForDesignDocTime = 5; //seconds

/**
 * An abstract class representing a Query.
 * 
 * - Has following read-only properties.
 *    - `queryId`: A unique id representing this query. A Database can't have 2 live queries with the same Id.
 *    - `database`: Instance of the `CouchdDB` against which this query is executed.
 *    - `value`: Represents the latest query value (snapshot)
 *    - `metadata`: Represents the latest metadata. e.g. `{inSync: true, localInSync: true, lastQueryTime: 0}`.
 *       - Initially, `inSync` will be `false` when query request is synced with
 *       remote database, then it will become `true`.
 *       - Initially, `localInSync` will be `false` when query request is synced with
 *       local database, then it will become `true`.
 *       - `lastQueryTime` holds last requested query time. It will also updated if query update is requested. 
 *       NOTE:: It won't update if query refreshed due to changes feed.
 *    - `query`: It's a plain Object, serialized format of the query. Will have at least 2 properties: `id` and `type`
 *    - `lastRefreshedAt`: Timestamp when query result or it's metadata is last changed, whichever is later.
 * - Emits `snapshot` event on this, when `value` is changed.
 * - Emits `metadata` event when `metadata` is changed.
 * - Emits `cancel` event when query is cancelled/stopped.
 */
class Query extends EventEmitter {

  constructor({ couchdb, queryId, localOnly, once, database, dispatch }) {
    super();
    this.couchdb = couchdb;
    this.queryId = queryId;
    this.localOnly = localOnly;
    this.once = once;
    this.database = database;
    this.dispatch = dispatch;
    this._lastRefreshedAt = undefined;
    this._metadata = {
      localInSync: false,
      inSync: false
    };

    //logger prefix
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.${this.database.dbName}.Query.${this.queryId}`;

    this._valPromise = new Promise((res, rej) => {
      this._resolveVal = res;
      this._rejectVal = rej;
    });
  }

  /**
   * Read-only property. Represents the serialized (plain JS Object) 
   * format of this query.
   * To be implemented by the child class.
   */
  get query() {
    throw "Not yet implemented";
  }

  /**
   * Read-only property. Represents the latest query value (snapshot)
   */
  get value() {
    return this._value;
  }

  /**
   * This method is used by all child query of this class.
   * It will set new value of query.
   * It will dispatch `snapshot` event.
   * It will also dispatch `querySnapshot` redux action
   * @param {Object} val new value 
   */
  _setValue(val) {
    this._value = val;
    this._lastRefreshedAt = new Date().getTime();
    this.emit('snapshot', this._value);
    this.dispatch(querySnapshot({
      couchdbName: this.couchdb.name,
      dbName: this.database.dbName,
      query: this.query,
      value: this._value
    }));
  }

  /**
   * Read-only property. Represents the latest metadata. e.g. `{inSync: true}`
   */
  get metadata() {
    return this._metadata;
  }

  /**
   * This method is used by all child query of this class.
   * It will set meta data of that query.
   * It will dispatch `metadata` event.
   * It will also dispatch `queryMetadata` redux action
   * @param {Object} 
   */
  _setMetadata(val) {
    this._metadata = { ...this._metadata, ...val };
    this._lastRefreshedAt = new Date().getTime();
    this.emit('metadata', this._metadata);
    this.dispatch(queryMetadata({
      couchdbName: this.couchdb.name,
      dbName: this.database.dbName,
      query: this.query,
      metadata: this._metadata
    }));
  }

  /**
   * Represents timestamp when either value or metadata is changed,
   * whichever is the latest.
   */
  get lastRefreshedAt() {
    return this._lastRefreshedAt;
  }

  /**
   * Default implementation throws an Error.
   * But, the child-class should implement this if it supports query update.
   * NOTE: once and localOnly options can't be updated. It's not supported by any query.
   */
  update() {
    throw new Error("update not supported");
  }

  /**
   * Stops listening for the live changes for this query (on local & remote both database).
   * Dispatches `cancel` event on this, and when redux is integrated dispatches corresponding redux action too.
   */
  cancel() {
    if (this.once) {
      this.log.warn(`${this.logPrefix}.cancel(): query with once can't cancelled.`);
      return;
    }

    this.cancelled = true;

    this._stopWatchLocalDbChanges();

    if(!this.localOnly) {
      this.database.remoteDb.cancelQuery(this.queryId);
    }

    this._dispatchCancel();
    this.log.debug(`${this.logPrefix}.cancel(): query is cancelled. query=`, this.query);
  }

  /**
   * Promise is resovled with the Query value whenever it becomes available.
   * If the Query is `localOnly` then Promise is resolved as soon as the value is available.
   * Otherwise, Promise is resolved only when `inSync=true`.
   */
  async val() {
    return this._valPromise;
  }

  /**
   * Listens `changes` event on localDb
   */
  _watchLocalDbChanges() {
    this.database.localDb.on('changes', this._onLocalDbChanges);
    this.log.debug(`${this.logPrefix}._watchLocalDbChanges: watching local database changes`);
  }

  /**
  * Removes `changes` event listener on localDb
  */
  _stopWatchLocalDbChanges() {
    this.database.localDb.off('changes', this._onLocalDbChanges);
    this.log.debug(`${this.logPrefix}._stopWatchLocalDbChanges: stopped watching local database changes`);
  }

  /**
   * Dispatches redux query action.
   */
  _dispatchQuery() {
    this.dispatch(query({
      couchdbName: this.couchdb.name,
      dbName: this.database.dbName,
      query: this.query
    }));
  }

  /**
   * Dispatches redux query update action.
   */
  _dispatchQueryUpdate() {
    this.dispatch(queryUpdate({
      couchdbName: this.couchdb.name,
      dbName: this.database.dbName,
      query: this.query
    }));
  }

  /**
   * Dispatches `cancel` event on this and redux action.
   */
  _dispatchCancel() {
    //Dispatch `cancel` event
    this.emit('cancel', this.queryId);

    //Dispatch Query cancel redux action
    this.dispatch(queryCancel({
      couchdbName: this.couchdb.name,
      dbName: this.database.dbName,
      query: this.query
    }));
  }

  /**
   * It waits till given design doc is available in local database.
   * @param {String} ddocId 
   */
  async _waitForDesignDoc(ddocId) {
    this._waitingForDDoc = true;
    const ddoc = await this.database.localDb.waitForDesignDoc(ddocId);
    this._cancelScheduler();
    this._waitingForDDoc = false;
    return ddoc.doc;
  }

  _startSchedulerForWarning() {
    this._scheduler = setTimeout(() => {
      this.log.warn(`${this.logPrefix}._startSchedulerForWarning: Design Doc isn’t yet available after ${waitForDesignDocTime} seconds of the Query completion on the server. Query={${JSON.stringify(this.query)}}. This could generally happen when Browser has cleaned up the local (pouchdb/IndexedDb) database when space was needed. This issue is unresolvable, so you need to dispose of the dreamdb. Consider requesting Persistence Storage permission from the User to avoid such issues.`);
    }, waitForDesignDocTime * 1000);
  }

  _cancelScheduler() {
    if(this._scheduler) {
      clearTimeout(this._scheduler);
      this._scheduler = undefined;
    }
  }

  /**
   * It dispatches `DOCS_UPDATED` redux action if changedDocs has documents which were available in oldResult but not 
   * in newResult.
   * @param {Array} oldResult old value of query
   * @param {Array} newResult new query result
   * @param {Array} changedDocs changed docs
   */
  _updateChangedDocs(oldResult, newResult, changedDocs) {
    if(!changedDocs || !changedDocs.length) {
      return;
    }

    let oldDocsById = keyBy(oldResult, '_id');
    let newDocsById = keyBy(newResult, '_id');

    let updatedDocs = changedDocs.filter((doc) => {
      if(oldDocsById[doc.id] && !newDocsById[doc.id]) {
        return true;
      }

      return false;
    });

    if(!updatedDocs.length) {
      return;
    }

    updatedDocs = map(updatedDocs, 'doc');

    this.dispatch(docsUpdated({
      couchdbName: this.couchdb.name,
      dbName: this.database.dbName,
      docs: updatedDocs
    }));
  }
}

export default Query;
